// NPC favorites library + browser world library (A4 parity for npm run dev).
import { parseWorldDraft, parseWorldSnapshot } from '../domain/parse.js';
import type { WizardStepId, WorldSnapshot } from '../domain/types.js';
import { resolveResumeStep } from './CreateWorld/wizardFlow.js';
import {
  compareWorldListItems,
  libraryMetadataFromDraft,
  libraryMetadataFromSnapshot,
  WORLD_LIBRARY_EMPTY_BODY,
  WORLD_LIBRARY_EMPTY_TITLE,
  WORLD_LIBRARY_LEGACY_UNMIGRATABLE,
  WORLD_LIBRARY_SORT_PREF_KEY,
  worldListItem,
  type WorldLibrarySort,
  type WorldListItem,
  type WorldRecordType,
} from '../db/worldLibrary.js';
import {
  createWorldDraft,
  resumeDraftFromSaved,
  saveDraftRecord,
  type PersistedDraftRecord,
  type SaveDraftResult,
} from '../db/worldPersistence.js';
import type { Memory, Npc } from '../types.js';
import { DRAFT_STORAGE_KEY, LAUNCH_HANDOFF_KEY, SNAPSHOT_STORAGE_KEY } from './CreateWorld/draftStorage.js';

export interface LegacyImportResult {
  imported: string[];
  skipped: string[];
  failed: string[];
  unmigratable: boolean;
}

/** Minimal storage reader for legacy import (mirrors scripts/legacyImport.ts). */
interface LegacyStorageReader {
  getItem(key: string): string | null;
}

const LEGACY_STORAGE_KEYS = {
  draft: DRAFT_STORAGE_KEY,
  snapshot: SNAPSHOT_STORAGE_KEY,
  handoff: LAUNCH_HANDOFF_KEY,
  characterBodies: 'specimen-observer:character-bodies',
} as const;

const LIB_KEY = 'converse:library';
const WORLD_STORE_KEY = 'specimen-observer:world-library-store';

export { WORLD_LIBRARY_EMPTY_BODY, WORLD_LIBRARY_EMPTY_TITLE, WORLD_LIBRARY_LEGACY_UNMIGRATABLE };

export interface LibraryEntry {
  npc: Npc;
  mems: Memory[];
  longterm: Array<[string, string]>;
}

interface StoredWorld {
  snapshot: WorldSnapshot;
  revision: number;
  roster?: Npc[];
}

interface WorldLibraryStore {
  drafts: Record<string, PersistedDraftRecord>;
  worlds: Record<string, StoredWorld>;
  legacyFailures: Record<string, string>;
  deletedIds: { draft: string[]; world: string[] };
}

function emptyWorldStore(): WorldLibraryStore {
  return { drafts: {}, worlds: {}, legacyFailures: {}, deletedIds: { draft: [], world: [] } };
}

function isTombstoned(store: WorldLibraryStore, id: string, recordType: WorldRecordType): boolean {
  return store.deletedIds[recordType].includes(id);
}

function addTombstone(store: WorldLibraryStore, id: string, recordType: WorldRecordType): void {
  const bucket = store.deletedIds[recordType];
  if (!bucket.includes(id)) bucket.push(id);
}

function removeTombstone(store: WorldLibraryStore, id: string, recordType: WorldRecordType): void {
  store.deletedIds[recordType] = store.deletedIds[recordType].filter((x) => x !== id);
}

/** Drop legacy singleton keys that still point at a deleted id. */
function clearLegacySingletonForDelete(id: string, recordType: WorldRecordType): void {
  try {
    if (recordType === 'draft') {
      const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { draftId?: string };
        if (parsed.draftId === id) localStorage.removeItem(DRAFT_STORAGE_KEY);
      }
      return;
    }
    const snapRaw = localStorage.getItem(SNAPSHOT_STORAGE_KEY);
    if (snapRaw) {
      const parsed = JSON.parse(snapRaw) as { worldId?: string };
      if (parsed.worldId === id) localStorage.removeItem(SNAPSHOT_STORAGE_KEY);
    }
    const handoffRaw = localStorage.getItem(LAUNCH_HANDOFF_KEY);
    if (handoffRaw) {
      const handoff = JSON.parse(handoffRaw) as { snapshot?: { worldId?: string } };
      if (handoff.snapshot?.worldId === id) localStorage.removeItem(LAUNCH_HANDOFF_KEY);
    }
  } catch {
    // Unreadable legacy storage — leave keys untouched.
  }
}

function readNpcLibrary(): LibraryEntry[] {
  try {
    const raw = JSON.parse(localStorage.getItem(LIB_KEY) ?? '[]') as LibraryEntry[];
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function writeNpcLibrary(entries: LibraryEntry[]): void {
  localStorage.setItem(LIB_KEY, JSON.stringify(entries));
}

function readWorldStore(): WorldLibraryStore {
  try {
    const raw = localStorage.getItem(WORLD_STORE_KEY);
    if (!raw) return emptyWorldStore();
    const parsed = JSON.parse(raw) as WorldLibraryStore;
    return {
      drafts: parsed.drafts ?? {},
      worlds: parsed.worlds ?? {},
      legacyFailures: parsed.legacyFailures ?? {},
      deletedIds: {
        draft: parsed.deletedIds?.draft ?? [],
        world: parsed.deletedIds?.world ?? [],
      },
    };
  } catch {
    return emptyWorldStore();
  }
}

function writeWorldStore(store: WorldLibraryStore): void {
  try {
    localStorage.setItem(WORLD_STORE_KEY, JSON.stringify(store));
  } catch {
    // Unwritable storage — session-only library.
  }
}

function draftListItem(record: PersistedDraftRecord): WorldListItem {
  const resume = resolveResumeStep(record.draft);
  const meta = libraryMetadataFromDraft(record.draft, record.revision);
  return worldListItem({ id: record.draft.draftId, recordType: 'draft', ...meta, wizardStep: resume });
}

function worldRowListItem(id: string, row: StoredWorld): WorldListItem {
  const meta = libraryMetadataFromSnapshot(row.snapshot, row.revision);
  return worldListItem({ id, recordType: 'world', ...meta });
}

/** Browser world library — mirrors SQLiteWorldLibraryRepository without better-sqlite3. */
export class BrowserWorldLibrary {
  private store: WorldLibraryStore = readWorldStore();

  private persist(): void {
    writeWorldStore(this.store);
  }

  getSortPreference(): WorldLibrarySort {
    try {
      const v = localStorage.getItem(WORLD_LIBRARY_SORT_PREF_KEY);
      if (v === 'name' || v === 'created' || v === 'recent') return v;
    } catch {
      // ignore
    }
    return 'recent';
  }

  setSortPreference(sort: WorldLibrarySort): void {
    try {
      localStorage.setItem(WORLD_LIBRARY_SORT_PREF_KEY, sort);
    } catch {
      // ignore
    }
  }

  hasUnmigratableLegacy(): boolean {
    return Object.keys(this.store.legacyFailures).length > 0;
  }

  list(sort?: WorldLibrarySort): WorldListItem[] {
    const activeSort = sort ?? this.getSortPreference();
    const items = [
      ...Object.values(this.store.drafts).map(draftListItem),
      ...Object.entries(this.store.worlds).map(([id, row]) => worldRowListItem(id, row)),
    ];
    return items.sort((a, b) => compareWorldListItems(a, b, activeSort));
  }

  resumeStep(id: string, recordType: WorldRecordType): WizardStepId | undefined {
    if (recordType === 'draft') {
      const record = this.store.drafts[id];
      return record ? resolveResumeStep(record.draft) : undefined;
    }
    return this.store.worlds[id] ? 'launched' : undefined;
  }

  getDraft(id: string): PersistedDraftRecord | undefined {
    return this.store.drafts[id];
  }

  getWorld(id: string): StoredWorld | undefined {
    return this.store.worlds[id];
  }

  saveDraft(record: PersistedDraftRecord): SaveDraftResult {
    const result = saveDraftRecord(record);
    removeTombstone(this.store, result.record.draft.draftId, 'draft');
    this.store.drafts[result.record.draft.draftId] = result.record;
    this.persist();
    return result;
  }

  upsertWorld(snapshot: WorldSnapshot, revision = 1, roster?: Npc[]): void {
    removeTombstone(this.store, snapshot.worldId, 'world');
    this.store.worlds[snapshot.worldId] = { snapshot, revision, roster };
    this.persist();
  }

  delete(id: string, recordType: WorldRecordType): boolean {
    if (recordType === 'draft') {
      if (!this.store.drafts[id]) return false;
      addTombstone(this.store, id, 'draft');
      delete this.store.drafts[id];
      clearLegacySingletonForDelete(id, 'draft');
      this.persist();
      return true;
    }
    if (recordType === 'world') {
      if (!this.store.worlds[id]) return false;
      addTombstone(this.store, id, 'world');
      delete this.store.worlds[id];
      clearLegacySingletonForDelete(id, 'world');
      this.persist();
      return true;
    }
    return false;
  }

  /** Idempotent legacy import — mirrors A4 script using browser store. */
  importLegacyLocalStorage(): LegacyImportResult {
    const reader: LegacyStorageReader = { getItem: (k) => localStorage.getItem(k) };
    const result: LegacyImportResult = { imported: [], skipped: [], failed: [], unmigratable: false };

    const draftRaw = reader.getItem(LEGACY_STORAGE_KEYS.draft);
    if (draftRaw) {
      try {
        const parsed = parseWorldDraft(JSON.parse(draftRaw));
        if (!parsed.ok) {
          result.failed.push(LEGACY_STORAGE_KEYS.draft);
          this.store.legacyFailures[LEGACY_STORAGE_KEYS.draft] = parsed.error;
        } else if (isTombstoned(this.store, parsed.value.draftId, 'draft')) {
          result.skipped.push(parsed.value.draftId);
        } else if (this.store.drafts[parsed.value.draftId]) {
          result.skipped.push(parsed.value.draftId);
        } else {
          this.store.drafts[parsed.value.draftId] = { draft: parsed.value, revision: 1 };
          result.imported.push(parsed.value.draftId);
        }
      } catch {
        result.failed.push(LEGACY_STORAGE_KEYS.draft);
        this.store.legacyFailures[LEGACY_STORAGE_KEYS.draft] = 'Malformed draft JSON';
      }
    }

    const importSnapshot = (raw: string, sourceKey: string): void => {
      try {
        const parsed = parseWorldSnapshot(JSON.parse(raw));
        if (!parsed.ok) {
          result.failed.push(sourceKey);
          this.store.legacyFailures[sourceKey] = parsed.error;
          return;
        }
        // parseWorldSnapshot zod output aligns with WorldSnapshot at runtime.
        const snapshot = parsed.value as WorldSnapshot;
        if (isTombstoned(this.store, snapshot.worldId, 'world')) {
          result.skipped.push(snapshot.worldId);
          return;
        }
        if (this.store.worlds[snapshot.worldId]) {
          result.skipped.push(snapshot.worldId);
          return;
        }
        this.store.worlds[snapshot.worldId] = { snapshot, revision: 1 };
        result.imported.push(snapshot.worldId);
      } catch {
        result.failed.push(sourceKey);
        this.store.legacyFailures[sourceKey] = 'Malformed snapshot JSON';
      }
    };

    const snapshotRaw = reader.getItem(LEGACY_STORAGE_KEYS.snapshot);
    if (snapshotRaw) importSnapshot(snapshotRaw, LEGACY_STORAGE_KEYS.snapshot);

    const handoffRaw = reader.getItem(LEGACY_STORAGE_KEYS.handoff);
    if (handoffRaw) {
      try {
        const handoff = JSON.parse(handoffRaw) as { snapshot?: unknown; roster?: Npc[] };
        if (handoff?.snapshot) {
          importSnapshot(JSON.stringify(handoff.snapshot), LEGACY_STORAGE_KEYS.handoff);
          const snap = parseWorldSnapshot(handoff.snapshot);
          if (
            snap.ok &&
            handoff.roster?.length &&
            !isTombstoned(this.store, snap.value.worldId, 'world')
          ) {
            this.store.worlds[snap.value.worldId] = {
              snapshot: snap.value as WorldSnapshot,
              revision: 1,
              roster: handoff.roster,
            };
          }
        }
      } catch {
        result.failed.push(LEGACY_STORAGE_KEYS.handoff);
        this.store.legacyFailures[LEGACY_STORAGE_KEYS.handoff] = 'Malformed handoff JSON';
      }
    }

    result.unmigratable = result.failed.length > 0 || this.hasUnmigratableLegacy();
    this.persist();
    return result;
  }

  resumeDraftRecord(id: string, step?: WizardStepId): PersistedDraftRecord | undefined {
    const record = this.store.drafts[id];
    if (!record) return undefined;
    const target = step ?? resolveResumeStep(record.draft);
    return resumeDraftFromSaved(record, target);
  }
}

let worldLibrary: BrowserWorldLibrary | null = null;

export function getWorldLibrary(): BrowserWorldLibrary {
  if (!worldLibrary) worldLibrary = new BrowserWorldLibrary();
  return worldLibrary;
}

/** Reset singleton (tests). */
export function resetWorldLibrary(): void {
  worldLibrary = null;
}

export function openWorldLibrary(): LegacyImportResult {
  return getWorldLibrary().importLegacyLocalStorage();
}

export function stashFavorites(
  npcs: Npc[],
  mems: Memory[],
  longterm: Map<string, string>,
): { stashed: number } {
  try {
    const saved: LibraryEntry[] = npcs
      .filter((n) => n.savedFavorite)
      .map((n) => ({
        npc: n,
        mems: mems.filter((m) => m.npcId === n.id || m.targetId === n.id),
        longterm: [...longterm.entries()].filter(([k]) => k.startsWith(`${n.id}>`)),
      }));
    const prev = readNpcLibrary();
    const seen = new Set(prev.map((e) => e.npc.id));
    const merged = [...prev, ...saved.filter((s) => !seen.has(s.npc.id))];
    localStorage.setItem(LIB_KEY, JSON.stringify(merged));
    return { stashed: saved.length };
  } catch {
    return { stashed: 0 };
  }
}

export function loadLibrary(): LibraryEntry[] {
  return readNpcLibrary();
}

export function removeSaved(npcId: string): LibraryEntry[] {
  const next = readNpcLibrary().filter((e) => e.npc.id !== npcId);
  try {
    writeNpcLibrary(next);
  } catch {
    // Unwritable storage: report the in-memory result anyway.
  }
  return next;
}

export function addCustom(npc: Npc): LibraryEntry[] | null {
  const prev = readNpcLibrary();
  if (prev.some((e) => e.npc.id === npc.id)) return null;
  const next = [...prev, { npc, mems: [], longterm: [] as Array<[string, string]> }];
  try {
    writeNpcLibrary(next);
  } catch {
    // Unwritable storage: report the in-memory result anyway.
  }
  return next;
}

export function cloneSavedForWorld(entry: LibraryEntry): Npc {
  const n = entry.npc;
  return {
    ...n,
    palette: { ...n.palette },
    nature: { ...n.nature },
    nurture: n.nurture.map((t) => ({ ...t })),
    state: { mood: 'neutral' as const, stress: 3, energy: 6 },
    savedFavorite: true,
  };
}

/** Seed helper for tests — creates an empty draft in the library store. */
export function seedWorldDraft(record?: PersistedDraftRecord): PersistedDraftRecord {
  const rec = record ?? createWorldDraft();
  getWorldLibrary().saveDraft(rec);
  return rec;
}
